import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getRoom, getRoomPatients, getRoomDevices, getRoomEvents, getRoomSensorReadings,
} from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import { useRelativeTime, formatDateTime } from '../lib/time';
import { StatusChip, SeverityDot } from '../components/StatusChip';
import ResidentAvatar from '../components/ResidentAvatar';
import { batteryColor } from '../lib/devices';
import { loadDeviceCatalog, deviceLabelFrom, deviceIconFrom } from '../lib/deviceCatalog';
import { SensorReadings } from '../components/SensorReadings';
import { SensorHistoryChart } from '../components/SensorHistoryChart';
import {
  ArrowLeft, DoorOpen, WindowsLogo, UsersThree, ArrowRight, Pulse, WifiHigh, WifiSlash,
} from '@phosphor-icons/react';

export default function RoomDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const rel = useRelativeTime();
  const roomId = decodeURIComponent(id);
  const [room, setRoom] = useState(null);
  const [patients, setPatients] = useState([]);
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [readings, setReadings] = useState([]);
  const [readingsLoading, setReadingsLoading] = useState(true);

  useEffect(() => { loadDeviceCatalog().then(setCatalog).catch(() => setCatalog([])); }, []);

  // Fan out the four reads in parallel. Each failure is swallowed locally
  // so a single bad endpoint doesn't blank the whole page.
  useEffect(() => {
    let alive = true;
    setRoom(null);
    setPatients([]);
    setDevices([]);
    setEvents([]);
    setReadings([]);

    getRoom(roomId)
      .then((r) => alive && setRoom(r))
      .catch(() => alive && setRoom(null));

    getRoomPatients(roomId)
      .then((list) => alive && setPatients(list))
      .catch(() => alive && setPatients([]));

    getRoomDevices(roomId)
      .then((list) => alive && setDevices(list))
      .catch(() => alive && setDevices([]));

    getRoomEvents(roomId)
      .then((list) => alive && setEvents(list))
      .catch(() => alive && setEvents([]));

    setReadingsLoading(true);
    getRoomSensorReadings(roomId)
      .then((list) => alive && setReadings(list))
      .catch(() => alive && setReadings([]))
      .finally(() => alive && setReadingsLoading(false));

    return () => { alive = false; };
  }, [roomId]);

  if (!room) return <div className="text-ink-muted">{t('loading')}</div>;

  // Only surface the climate history card when the room actually has an H4 Pro
  // (a sensor device assigned, or any temperature/humidity signal available).
  const hasClimate =
    devices.some((d) => (d.type || d.raw_type) === 'h4pro')
    || room.last_temperature != null
    || room.last_humidity != null
    || readings.length > 0;

  return (
    <div className="space-y-8 animate-fade-up">
      <Link
        to="/rooms"
        data-testid="back-to-rooms"
        className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink"
      >
        <ArrowLeft size={16} /> {t('rooms_title')}
      </Link>

      <header className="grid lg:grid-cols-[1fr_2fr] gap-6">
        <div className="bg-bg-alt rounded-2xl border border-stone_line p-8">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
            {t('room')}
          </div>
          <h1 className="font-heading text-4xl text-ink mt-1">{room.room_name}</h1>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {room.room_type && (
              <span className="text-[10px] uppercase tracking-[0.18em] text-sage-600 bg-sage-100 px-2 py-1 rounded-full">
                {t(`room_type_${room.room_type}`)}
              </span>
            )}
            {room.floor != null && (
              <span className="text-xs text-ink-muted">{t('room_floor')} {room.floor}</span>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone_line p-8 shadow-soft">
          <div className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold mb-4">
            {t('room_state')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StateCard
              icon={DoorOpen}
              label={t('door')}
              value={t(`door_${room.door_state || 'unknown'}_state`)}
              state={room.door_state || 'unknown'}
            />
            <StateCard
              icon={WindowsLogo}
              label={t('window')}
              value={t(`door_${room.window_state || 'unknown'}_state`)}
              state={room.window_state || 'unknown'}
            />
            <StateCard
              icon={UsersThree}
              label={t('room_patients')}
              value={String(room.patient_count ?? patients.length)}
              state="ok"
            />
          </div>
          {(room.last_temperature != null || room.last_humidity != null) && (
            <div className="mt-4">
              <SensorReadings
                temperature={room.last_temperature}
                humidity={room.last_humidity}
                at={room.last_reading_at}
                testid="room-sensor-readings"
              />
            </div>
          )}
          {room.corpus_name && (
            <div className="mt-4 text-xs text-ink-muted">
              {t('admin_field_building')}: <span className="text-ink">{room.corpus_name}</span>
            </div>
          )}
        </div>
      </header>

      <section className="grid lg:grid-cols-2 gap-6">
        <Card title={t('room_patients')} count={patients.length}>
          {patients.length === 0 ? (
            <div className="text-sm text-ink-muted py-3">{t('room_no_patients')}</div>
          ) : (
            <ul className="space-y-2" data-testid="room-patients-list">
              {patients.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/patients/${p.id}`}
                    data-testid={`room-patient-${p.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-stone_line hover:border-sage/30 hover:bg-bg-alt/50 transition-all"
                  >
                    <ResidentAvatar name={p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim()} src={p.avatar_url} size={40} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink truncate">
                        {p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—'}
                      </div>
                    </div>
                    {p.status && <StatusChip status={p.status} />}
                    <ArrowRight size={14} className="text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t('room_devices')} count={devices.length}>
          {devices.length === 0 ? (
            <div className="text-sm text-ink-muted py-3">{t('room_no_devices')}</div>
          ) : (
            <ul className="space-y-2" data-testid="room-devices-list">
              {devices.map((d) => {
                const Icon = deviceIconFrom(catalog, d.type);
                // Display name from the catalog; degrade to a humanised raw value.
                const label = deviceLabelFrom(catalog, d.type)
                  || (d.raw_type ? String(d.raw_type).replace(/_/g, ' ') : d.model || t('device_unknown') || 'Unknown device');
                return (
                  <li key={d.id}>
                    <Link
                      to={`/devices/${d.id}`}
                      data-testid={`room-device-${d.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl border border-stone_line hover:border-sage/30 hover:bg-bg-alt/50 transition-all"
                    >
                      <div className="w-10 h-10 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                        <Icon size={18} weight="duotone" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink capitalize">{label}</div>
                        <div className="text-xs text-ink-muted font-mono truncate">{d.mac_address || d.id}</div>
                      </div>
                      {d.battery != null && (
                        <div className={`text-xs ${batteryColor(d.battery)}`}>{d.battery}%</div>
                      )}
                      <StatusChip status={d.connectivity_status} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </section>

      {hasClimate && (
        <Card title={t('sensor_history_title')}>
          <SensorHistoryChart readings={readings} loading={readingsLoading} />
        </Card>
      )}

      <Card title={t('room_openings')} count={room.openings ? room.openings.length : null}>
        {!room.openings || room.openings.length === 0 ? (
          <div className="text-sm text-ink-muted py-3">{t('room_no_openings')}</div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="room-openings-list">
            {room.openings.map((o) => {
              const Icon = o.sensor_target === 'window' ? WindowsLogo : DoorOpen;
              const stateTone =
                o.state === 'open' ? 'bg-terracotta-100 text-terracotta-700 border-terracotta/30'
                  : o.state === 'closed' ? 'bg-sage-100 text-sage-700 border-sage/30'
                    : 'bg-bg-alt text-ink-muted border-stone_line';
              return (
                <li
                  key={o.device_id}
                  data-testid={`room-opening-${o.device_id}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-stone_line bg-white"
                >
                  <div className="w-10 h-10 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                    <Icon size={18} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">
                      {o.sensor_label || (o.sensor_target ? t(`sensor_target_${o.sensor_target}`) : t('sensor_target_unset'))}
                    </div>
                    <div className="text-xs text-ink-muted font-mono truncate">
                      {o.mac || o.device_id}
                    </div>
                    {o.last_event_at && (
                      <div className="text-[11px] text-ink-muted mt-0.5">
                        {t('opening_last_event')}: {rel(o.last_event_at)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${stateTone}`}
                    >
                      {t(`door_${o.state || 'unknown'}_state`)}
                    </span>
                    <div className="flex items-center gap-1 text-[11px] text-ink-muted">
                      {o.online ? <WifiHigh size={12} /> : <WifiSlash size={12} className="text-terracotta-700" />}
                      {o.battery != null && <span className="font-mono">{o.battery}%</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card
        title={t('room_activity')}
        action={
          <Link
            to={`/events?room=${encodeURIComponent(roomId)}`}
            data-testid="room-activity-view-all"
            className="text-xs text-sage-600 hover:text-sage-700 inline-flex items-center gap-1"
          >
            {t('room_activity_view_all')} <ArrowRight size={12} />
          </Link>
        }
      >
        {events.length === 0 ? (
          <div className="text-sm text-ink-muted py-3">{t('no_results')}</div>
        ) : (
          <ol className="relative border-l border-stone_line ml-3" data-testid="room-activity-list">
            {events.slice(0, 5).map((ev) => (
              <li key={ev.id} className="ml-6 pb-5 last:pb-0">
                <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-white border-2 border-stone_line grid place-items-center">
                  <SeverityDot severity={ev.severity} />
                </span>
                <div className="flex items-start gap-3">
                  <Pulse size={16} className="text-ink-soft mt-0.5" weight="duotone" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 justify-between">
                      <div className="text-sm font-medium text-ink">{t(`event_${ev.type}`)}</div>
                      <div className="text-xs text-ink-muted whitespace-nowrap">
                        {formatDateTime(ev.created_at, lang)} · {rel(ev.created_at)}
                      </div>
                    </div>
                    <div className="text-xs text-ink-soft mt-1">
                      {ev.resident_name || ev.patient_name || ''}
                      {ev.message ? ` · ${ev.message}` : ''}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function StateCard({ icon: Icon, label, value, state, sub }) {
  const tone = state === 'open' ? 'text-terracotta-700 bg-terracotta-100'
    : state === 'closed' ? 'text-sage-600 bg-sage-100'
    : 'text-ink-soft bg-bg-alt';
  return (
    <div className="rounded-xl border border-stone_line p-4">
      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${tone}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">{label}</div>
      <div className="text-base text-ink font-medium mt-1">{value}</div>
      {sub && <div className="text-[11px] text-ink-muted mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function Card({ title, count, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-stone_line p-6 shadow-soft">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl text-ink">{title}</h2>
        <div className="flex items-center gap-3">
          {count != null && <span className="text-xs text-ink-muted">{count}</span>}
          {action}
        </div>
      </div>
      {children}
    </div>
  );
}
